import { Upload, AlertCircle, HardDrive } from "lucide-react";
import { useState } from "react";
import DocumentItem from "./DocumentItem";
import { Label } from "../ui/label";
import { DOCUMENT_ACCEPT, DOCUMENT_ACCEPT_LABEL } from "@/utils/uploadFileTypes";

export interface DocumentFile {
  id: string;
  file: File;
  fileName: string;
  size: number;
  type: string;
  createdAt: string;
}

export default function DocumentMultyUploadSection({
  documents,
  onAdd,
  onRemove,
  maxDocuments = 4,
}: {
  documents: DocumentFile[];
  onAdd: (files: FileList) => void;
  onRemove: (doc: DocumentFile) => void;
  maxDocuments?: number;
}) {
  const [isDragging, setIsDragging] = useState(false);

  // Derived state for UI feedback
  const isMaxReached = documents.length >= maxDocuments;
  //   const remainingSlots = maxDocuments - documents.length;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
      e.target.value = ""; // Reset input
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!isMaxReached) setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (
      !isMaxReached &&
      e.dataTransfer.files &&
      e.dataTransfer.files.length > 0
    ) {
      processFiles(e.dataTransfer.files);
    }
  };

  const processFiles = (files: FileList) => {
    const slots = maxDocuments - documents.length;
    if (slots <= 0) return;

    const filesToAdd = Math.min(files.length, slots);
    if (filesToAdd > 0) {
      const limitedFiles = Array.from(files).slice(0, filesToAdd);
      const dataTransfer = new DataTransfer();
      limitedFiles.forEach((file) => dataTransfer.items.add(file));
      onAdd(dataTransfer.files);
    }
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-0.5">
          <Label className="text-sm  text-slate-900">
            Supporting Documents
          </Label>
          <p className="text-[12px] text-slate-500">
            Upload requirements for this stage.
          </p>
        </div>

        {/* Usage Badge */}
        <div
          className={`
          flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border
          ${
            isMaxReached
              ? "bg-amber-50 text-amber-700 border-amber-200"
              : "bg-slate-50 text-slate-600 border-slate-200"
          }
        `}
        >
          <HardDrive className="size-3" />
          <span>
            {documents.length} / {maxDocuments}
          </span>
        </div>
      </div>

      {/* Upload Zone - Maintains layout even when full */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          relative group flex flex-col items-center justify-center gap-3 
          min-h-[140px] px-6 py-8 rounded-xl border-2 border-dashed 
          transition-all duration-300 ease-in-out
          ${
            isMaxReached
              ? "bg-slate-50 border-slate-200 cursor-not-allowed opacity-60 grayscale"
              : isDragging
                ? "bg-primary/5 border-primary scale-[0.99] ring-4 ring-primary/10"
                : "bg-white border-slate-200 hover:border-primary/50 hover:bg-slate-50/50 cursor-pointer"
          }
        `}
      >
        {!isMaxReached && (
          <input
            type="file"
            id="document-upload"
            multiple
            accept={DOCUMENT_ACCEPT}
            onChange={handleFileChange}
            className="hidden"
          />
        )}

        <label
          htmlFor={!isMaxReached ? "document-upload" : undefined}
          className={`flex flex-col items-center justify-center w-full h-full ${!isMaxReached ? "cursor-pointer" : "cursor-not-allowed"}`}
        >
          {isMaxReached ? (
            // Max Reached State
            <div className="flex flex-col items-center gap-2 text-center animate-in fade-in zoom-in duration-300">
              <div className="p-3 bg-amber-100/50 rounded-full">
                <AlertCircle className="size-6 text-amber-600/70" />
              </div>
              <div className="space-y-0.5">
                <p className="text-sm font-medium text-slate-900">
                  Limit Reached
                </p>
                <p className="text-xs text-slate-500">
                  Remove a file to upload more
                </p>
              </div>
            </div>
          ) : (
            // Active Upload State
            <>
              <div
                className={`
                p-3.5 mb-2 rounded-full transition-colors duration-300
                ${isDragging ? "bg-primary/20 text-primary" : "bg-slate-100 text-slate-500 group-hover:bg-primary/10 group-hover:text-primary"}
              `}
              >
                <Upload className="size-6" strokeWidth={2} />
              </div>

              <div className="text-center space-y-1">
                <p className="text-sm font-medium text-slate-700 group-hover:text-slate-900 transition-colors">
                  <span className="text-primary underline-offset-4 group-hover:underline">
                    Click to upload
                  </span>{" "}
                  or drag and drop
                </p>
                <p className="text-xs text-slate-400">
                  {DOCUMENT_ACCEPT_LABEL} up to 5MB
                </p>
              </div>
            </>
          )}
        </label>
      </div>

      {/* File List - Animated Entry */}
      {documents.length > 0 && (
        <div className="flex flex-col gap-2.5 animate-in slide-in-from-top-2 duration-300">
          {/* You could add a tiny header here like "Attached Files" if the list gets long */}
          {documents.map((doc) => (
            <DocumentItem key={doc.id} doc={doc} onDelete={onRemove} />
          ))}
        </div>
      )}
    </div>
  );
}
